import React, { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { getProductionSummary } from '../services/projectService';
import useUIStore from '../store/uiStore';
import ProjectProgressPanel from '../components/ProjectProgressPanel';

const dateStr = (d) => d.toISOString().slice(0, 10);
const todayStr = () => dateStr(new Date());
const monthsAgoStr = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return dateStr(d);
};

const fmtHours = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 });
const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Same dependency-free CSS bar chart approach as PaymentsBarChart/
// ExpenseCharts elsewhere in the app - here generalized with a custom
// formatter and a click handler per row, since these bars are both a
// man-hours view and a cost view of the same project list, and clicking
// either one drills into that project's detail below.
const ProjectBarChart = ({ rows, valueKey, format, tone, onSelect, emptyText }) => {
  const max = Math.max(1, ...rows.map((r) => r[valueKey]));
  return rows.length === 0 ? (
    <p className="text-xs text-gray-400">{emptyText}</p>
  ) : (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <button
          key={r.id}
          onClick={() => onSelect(r.id)}
          className="w-full text-left group"
        >
          <div className="flex justify-between text-xs mb-1">
            <span className="text-gray-700 group-hover:text-komal-blue truncate pr-2">{r.name}</span>
            <span className="font-medium text-gray-900 whitespace-nowrap">{format(r[valueKey])}</span>
          </div>
          <div className="bg-gray-100 rounded h-2.5 group-hover:bg-gray-200">
            <div
              className={`h-2.5 rounded ${tone}`}
              style={{ width: `${Math.max((r[valueKey] / max) * 100, 2)}%` }}
            />
          </div>
        </button>
      ))}
    </div>
  );
};

const ProductionTrackingPage = () => {
  const showToast = useUIStore((s) => s.showToast);
  // Default to the last 3 months so there's a useful window on first load,
  // rather than a single calendar month with no way to widen it.
  const [startDate, setStartDate] = useState(monthsAgoStr(3));
  const [endDate, setEndDate] = useState(todayStr());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  useEffect(() => {
    setLoading(true);
    getProductionSummary({ startDate, endDate })
      .then((res) => setSummary(res.data))
      .catch((err) => showToast(err.response?.data?.detail || 'Could not load production summary', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const projects = summary?.projects || [];
  const totalManHours = projects.reduce((sum, p) => sum + p.man_hours, 0);
  const totalCost = projects.reduce((sum, p) => sum + p.all_in_cost, 0);
  const byManHours = [...projects].sort((a, b) => b.man_hours - a.man_hours);
  const byCost = [...projects].sort((a, b) => b.all_in_cost - a.all_in_cost);
  const selectedProject = projects.find((p) => p.id === selectedProjectId);

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-white">Production Tracking</h1>
          <p className="text-sm text-white/70 mt-1">Man-hours and cost across every project for the selected period.</p>
        </div>

        <div className="flex flex-wrap gap-3 mb-5 items-end">
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-white/70 mb-1">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          {selectedProjectId && (
            <button
              onClick={() => setSelectedProjectId(null)}
              className="flex items-center gap-1.5 text-sm font-semibold text-white hover:underline ml-auto"
            >
              <ArrowLeft size={14} /> Back to all projects
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-sm text-white/70">Loading…</p>
        ) : selectedProject ? (
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">{selectedProject.name}</h2>
              <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 capitalize">{selectedProject.status?.replace('_', ' ')}</span>
            </div>
            <ProjectProgressPanel projectId={selectedProject.id} startDate={startDate} endDate={endDate} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 mb-5">
              <div className="glass-card p-4">
                <div className="text-xs text-gray-500 mb-1">Total man-hours (all projects)</div>
                <div className="text-2xl font-bold text-gray-900">{fmtHours(totalManHours)}</div>
              </div>
              <div className="glass-card p-4">
                <div className="text-xs text-gray-500 mb-1">Total all-in cost (all projects)</div>
                <div className="text-2xl font-bold text-gray-900">{fmtMoney(totalCost)}</div>
              </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
              <div className="glass-card p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-4">Man-hours by project</h2>
                <ProjectBarChart
                  rows={byManHours}
                  valueKey="man_hours"
                  format={fmtHours}
                  tone="bg-komal-blue"
                  onSelect={setSelectedProjectId}
                  emptyText="No daily entries logged in this range."
                />
              </div>
              <div className="glass-card p-5">
                <h2 className="text-sm font-semibold text-gray-900 mb-4">Cost by project</h2>
                <ProjectBarChart
                  rows={byCost}
                  valueKey="all_in_cost"
                  format={fmtMoney}
                  tone="bg-green-600"
                  onSelect={setSelectedProjectId}
                  emptyText="No cost recorded in this range."
                />
              </div>
            </div>

            <p className="text-xs text-white/60 mt-4">Click a project's bar to see its detailed progress, output, and worker breakdown for this period.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductionTrackingPage;
